import * as React from 'react';
import { FC, useState, useEffect, ChangeEventHandler, FocusEventHandler } from 'react';
import { isBlank } from './utils';
import { UpdateValidationState } from './useValidationState';

export type FieldProps = {
  name: string;
  label: string;
  type?: string;
  hint?: string;
  placeholder?: string;
  maxLength?: number;
  minLength?: number;
  length?: number;
  required?: boolean;
  pattern?: string;
  view?: 'phoneWithoutCode';
  validation?: 'required' | 'phoneWithoutCode' | 'phoneWithCode' | 'card' | 'html5';
  updateValidationState: UpdateValidationState;
};

const useUpdateFieldValidationState = (props: FieldProps, value: string): ((v: string) => void) => {
  const { name, maxLength, minLength, length, required, pattern, updateValidationState, validation } = props;

  const html5Validations = (v: string) =>
    (maxLength ? v.length <= maxLength : true) &&
    (minLength ? v.length >= minLength : true) &&
    (length ? v.length === length : true) &&
    (required ? !isBlank(v) : true) &&
    (pattern ? new RegExp(pattern).test(v) : true);

  const isValid = (v: string) => {
    switch (validation) {
      case 'required':
        return !isBlank(v);
      case 'phoneWithoutCode':
        return /^\d{10}$/.test(v);
      case 'phoneWithCode':
        return /^\d+$/.test(v);
      case 'card':
        return /^\d{13,19}$/.test(v);
      case 'html5':
        return html5Validations(v);
      default:
        return true;
    }
  };

  const updateFieldValidationState = (v: string) => {
    if (validation) {
      updateValidationState({ field: name, isValid: isValid(v) });
    }
  };

  useEffect(() => {
    updateFieldValidationState(value);
    return () => updateValidationState({ field: name, isValid: true });
  }, []);

  return updateFieldValidationState;
};

const Field: FC<FieldProps> = (props) => {
  const { name, label, type, hint, placeholder, view } = props;
  const [value, setValue] = useState('');
  const [tips, setTips] = useState(false);
  const updateFieldValidationState = useUpdateFieldValidationState(props, value);

  const onChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const newValue = e.target.value;
    setValue(newValue);
    updateFieldValidationState(newValue);
  };

  const onFocus: FocusEventHandler<HTMLInputElement> = (e) => {
    setTips(true);
    e.target.placeholder = '';
  };

  const onBlur: FocusEventHandler<HTMLInputElement> = (e) => {
    setTips(false);
    e.target.placeholder = placeholder || '';
  };

  const input = () => (
    <input
      type={type || 'text'}
      name={name}
      id={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoComplete="off"
      onFocus={onFocus}
      onBlur={onBlur}
    />
  );

  const phoneWithoutCode = () => (
    <div className="rs-flex rs-flex_collapse">
      <div className="rs-flex-item rs-small-1">
        <span className="prefix">+7</span>
      </div>
      <div className="rs-flex-item rs-small-11">{input()}</div>
    </div>
  );

  return (
    <div className="rs-row">
      <div className={`rs-column-small-12 ${placeholder && tips ? 'tips_block' : ''}`}>
        <label htmlFor={name}>{`${label}:`}</label>
        {view === 'phoneWithoutCode' ? phoneWithoutCode() : input()}
        {placeholder && tips ? <p className="tips_text">{placeholder}</p> : null}
        {hint ? <p className="rs-text-size_small">{hint}</p> : null}
      </div>
    </div>
  );
};

export default Field;
